import { Box, Paper, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { Table } from 'Common/MUITableV2'
import {
  formatInt,
  formatNumber,
  formatPct,
  useUtilisationData,
} from './Utilisation.data'
import type { AssetUtilisationRow } from './Utilisation.types'

type Props = {
  schedulerId: string
  iteration: string
}

const HOURS_COLS: GridColDef<AssetUtilisationRow>[] = [
  { field: 'asset_name', headerName: 'Asset', flex: 1, minWidth: 90 },
  {
    field: 'planned_hrs',
    headerName: 'Planned',
    type: 'number',
    flex: 1,
    minWidth: 80,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'productive_hrs',
    headerName: 'Productive',
    type: 'number',
    flex: 1,
    minWidth: 90,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'cleaning_hrs',
    headerName: 'Cleaning',
    type: 'number',
    flex: 1,
    minWidth: 80,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'available_hrs',
    headerName: 'Available',
    type: 'number',
    flex: 1,
    minWidth: 90,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'unused_hrs',
    headerName: 'Unused',
    type: 'number',
    flex: 1,
    minWidth: 80,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'utilisation_pct',
    headerName: 'Utilisation',
    type: 'number',
    flex: 1,
    minWidth: 90,
    valueFormatter: (value: number) => formatPct(value),
  },
  {
    field: 'productive_pct',
    headerName: 'Productive',
    type: 'number',
    flex: 1,
    minWidth: 90,
    valueFormatter: (value: number) => formatPct(value),
  },
  {
    field: 'cleaning_pct',
    headerName: 'Cleaning',
    type: 'number',
    flex: 1,
    minWidth: 80,
    valueFormatter: (value: number) => formatPct(value),
  },
]

// Kitchens add "Capacity used": how much of what the kitchen could make its
// lines actually drew. "Utilisation" only says how long it was busy, and a
// kitchen feeding one slow line all day is busy 100% of it while nowhere near
// full. Shown as "—" for a run made before scheduler_rust 7.161.0, which did
// not report it.
const KITCHEN_COLS: GridColDef<AssetUtilisationRow>[] = [
  ...HOURS_COLS.slice(0, 7),
  {
    field: 'capacity_used_pct',
    headerName: 'Capacity used',
    type: 'number',
    flex: 1,
    minWidth: 110,
    valueFormatter: (value: number | null) => formatPct(value ?? NaN),
  },
  ...HOURS_COLS.slice(7),
]

const CIP_COLS: GridColDef<AssetUtilisationRow>[] = [
  { field: 'asset_name', headerName: 'Asset', flex: 1, minWidth: 100 },
  {
    field: 'planned_hrs',
    headerName: 'Planned',
    type: 'number',
    flex: 1,
    minWidth: 90,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'unused_hrs',
    headerName: 'Unused',
    type: 'number',
    flex: 1,
    minWidth: 90,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'available_hrs',
    headerName: 'Available',
    type: 'number',
    flex: 1,
    minWidth: 100,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'utilisation_pct',
    headerName: 'Utilisation',
    type: 'number',
    flex: 1,
    minWidth: 100,
    valueFormatter: (value: number) => formatPct(value),
  },
]

const TableSection = ({
  title,
  rows,
  columns,
  loading = false,
}: {
  title: string
  rows: AssetUtilisationRow[]
  columns: GridColDef<AssetUtilisationRow>[]
  /** Draws the grid's skeleton overlay in place of rows. */
  loading?: boolean
}) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Typography
      variant="subtitle2"
      sx={{
        textAlign: 'center',
        fontWeight: 700,
        color: 'primary.main',
        mb: 1,
      }}
    >
      {title}
    </Typography>
    <Table
      loading={loading}
      // The skeleton draws ceil(viewportHeight / rowHeight) rows, and this
      // grid is `autoHeight` — with no rows that viewport is zero and the
      // overlay comes out empty. The floor only applies while loading.
      sx={loading ? { minHeight: 220 } : undefined}
      rows={rows}
      columns={columns as GridColDef[]}
      getRowId={(r: any) => `${r.asset_type}-${r.asset_name}`}
      density="compact"
      hideFooter
      disableRowSelectionOnClick
      autoHeight
      getRowClassName={(p) =>
        (p.indexRelativeToCurrentPage ?? 0) % 2 === 0 ? 'row--even' : 'row--odd'
      }
    />
  </Paper>
)

const Kpi = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      px: 2,
      py: 1,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      flex: 1,
      minWidth: 160,
    }}
  >
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{value}</Typography>
  </Box>
)

export const AssetUtilisationView = ({ schedulerId, iteration }: Props) => {
  const { data, isLoading, error } = useUtilisationData(schedulerId, iteration)

  // The real three-section layout with skeleton grids, so nothing moves when
  // the data lands. The KPI strip is left out deliberately — four numbers
  // shown as zeros would read as a result rather than as a wait.
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TableSection
            title="Kitchen Utilisation"
            rows={[]}
            columns={KITCHEN_COLS}
            loading
          />
          <TableSection title="CIP" rows={[]} columns={CIP_COLS} loading />
        </Box>
        <TableSection
          title="Packing Utilisation"
          rows={[]}
          columns={HOURS_COLS}
          loading
        />
      </Box>
    )
  }
  if (error || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Could not load utilisation data
          {error instanceof Error ? `: ${error.message}` : ''}.
        </Typography>
      </Box>
    )
  }

  const kitchens = data.asset_utilisation.filter(
    (r) => r.asset_type === 'kitchen'
  )
  const lines = data.asset_utilisation.filter((r) => r.asset_type === 'line')
  const cips = data.asset_utilisation.filter((r) => r.asset_type === 'cip')

  return (
    <Box>
      {/* KPI strip — stretches to fill row */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Kpi
          label="Number of HALBs"
          value={formatInt(data.summary.num_halbs)}
        />
        <Kpi
          label="Total Plan Tonnage"
          value={formatNumber(data.summary.total_tonnage_kg / 1000, 0)}
        />
        <Kpi label="Number of FGs" value={formatInt(data.summary.num_fgs)} />
        <Kpi
          label="Total Plan Cases"
          value={formatInt(data.summary.total_cases)}
        />
      </Box>

      {/* Kitchen + Packing tables side-by-side, CIP under Kitchen */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TableSection
            title="Kitchen Utilisation"
            rows={kitchens}
            columns={KITCHEN_COLS}
          />
          <TableSection title="CIP" rows={cips} columns={CIP_COLS} />
        </Box>
        <TableSection
          title="Packing Utilisation"
          rows={lines}
          columns={HOURS_COLS}
        />
      </Box>

      {/* Max Clean End Time footer */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>Max Clean End Time</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
            Grand Total
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {formatNumber(data.summary.max_clean_end_time, 2)}
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}
